import React from "react";
import { captureComponentError } from "../../lib/telemetry";

const SUPPORT_EMAIL =
  import.meta.env.VITE_SUPPORT_EMAIL || "support@odonboard.com";

function ClearSiteDataButton() {
  async function clearAll() {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }
      localStorage.clear();
      sessionStorage.clear();
    } finally {
      window.location.reload();
    }
  }

  return (
    <button
      onClick={clearAll}
      className="mt-3 inline-flex items-center justify-center rounded-[10px] border border-border bg-white px-4 py-2 text-sm font-medium text-foreground shadow-sm transition-all duration-150 hover:bg-panel hover:border-[color:var(--color-border-strong)]"
    >
      Clear site data &amp; reload
    </button>
  );
}

interface RootBoundaryProps {
  children: React.ReactNode;
}

interface RootBoundaryState {
  err?: Error;
}

export class RootBoundary extends React.Component<RootBoundaryProps, RootBoundaryState> {
  constructor(props: RootBoundaryProps) {
    super(props);
    this.state = { err: undefined };
  }

  static getDerivedStateFromError(err: Error) {
    return { err };
  }

  componentDidCatch(err: Error, info: React.ErrorInfo) {
    captureComponentError(err, info, "RootBoundary");
  }

  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            {/* Brand mark */}
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand shadow-[0_4px_20px_rgba(31,42,68,0.18)]">
              <span className="text-xl font-bold text-white">OD</span>
            </div>

            <h1 className="mb-3 text-2xl font-semibold tracking-tight text-foreground">
              Something went wrong
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
              An unexpected error occurred. Your data is safe — try clearing
              your browser cache or contact support if the issue persists.
            </p>

            <div className="flex flex-col items-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-[10px] bg-brand px-5 py-2.5 text-sm font-medium text-white shadow-[0_1px_3px_rgba(31,42,68,0.25)] transition-all duration-150 hover:bg-brand-hover"
              >
                Reload page
              </button>
              <ClearSiteDataButton />
            </div>

            {/* Collapsible error detail */}
            <details className="mt-8 rounded-xl border border-border bg-white p-4 text-left">
              <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                Error details
              </summary>
              <p className="mt-3 text-xs font-medium text-foreground break-words">
                {this.state.err?.message || String(this.state.err)}
              </p>
              {this.state.err?.stack && (
                <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words text-meta leading-relaxed text-muted-foreground">
                  {this.state.err.stack}
                </pre>
              )}
            </details>

            <p className="mt-6 text-xs text-muted-foreground">
              Need help?{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium text-brand hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
